/**
 * Cera campaign helper.
 *
 * Landing URL schema (see cera.coop "PRODUCTEN bestellen via website of platform van de partner"):
 *   vn = vennotennummer           (unique Cera member number - unlock code)
 *   ve = vennoot e-mailadres
 *   ln = achternaam (last name)
 *   fn = voornaam  (first name)
 *   st = straatnaam
 *   nr = huisnummer
 *   bn = bus
 *   pc = postcode
 *   gm = gemeente
 *   tn = telefoonnummer
 *   nb = benefit / actiecode op de Cera-website
 *
 * Example landing URL:
 *   https://kriket.be/products/<cera-product-handle>?vn=726533073280
 *     &ve=renate.bovens%40cera.coop&ln=BOVENS&fn=RENATE
 *     &st=BIERBEEKSTRAAT&nr=21&bn=&pc=3360&gm=BIERBEEK
 *     &tn=0475123456&nb=CERA-ACTIE-2026
 *
 * Behaviour:
 * 1. On every page load, any of the params above found in the URL are stored in localStorage
 *    under the key `ceraParams` (merged with what was already there).
 * 2. `window.ceraCampaign.goToCheckout(variantId)` adds the variant to the cart (with
 *    vennotennummer + benefit code as line-item properties), writes all params onto the cart
 *    as cart attributes and then redirects straight to `/checkout` with Shopify's prefill
 *    query params so the shipping address / email / phone come in pre-filled.
 * 3. The standard "proceed to checkout" links/buttons of the theme are hijacked so that the
 *    same prefill is applied whenever Cera params are present in localStorage - so the
 *    regular cart flow benefits from it too.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ceraParams';

  // Mapping of URL short-codes -> human readable label used as cart attribute key.
  var ATTRIBUTE_LABELS = {
    vn: 'Vennotennummer',
    ve: 'Vennoot e-mail',
    ln: 'Vennoot naam',
    fn: 'Vennoot voornaam',
    st: 'Vennoot straat',
    nr: 'Vennoot huisnummer',
    bn: 'Vennoot bus',
    pc: 'Vennoot postcode',
    gm: 'Vennoot gemeente',
    tn: 'Vennoot telefoonnummer',
    nb: 'Cera benefit code'
  };

  function getQueryParams() {
    var result = {};
    var search = (window.location.search || '').replace(/^\?/, '');
    if (!search) return result;
    var pairs = search.split('&');
    for (var i = 0; i < pairs.length; i++) {
      if (!pairs[i]) continue;
      var idx = pairs[i].indexOf('=');
      var key, value;
      if (idx === -1) {
        key = pairs[i];
        value = '';
      } else {
        key = pairs[i].slice(0, idx);
        value = pairs[i].slice(idx + 1);
      }
      try {
        key = decodeURIComponent(key);
        value = decodeURIComponent(value.replace(/\+/g, ' '));
      } catch (e) {
        // ignore malformed param
      }
      result[key] = value;
    }
    return result;
  }

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function writeStored(data) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // localStorage might be disabled (private mode, quota, ...)
      if (window.console && console.warn) {
        console.warn('[cera] unable to persist params', e);
      }
    }
  }

  function captureFromUrl() {
    var params = getQueryParams();
    var captured = {};
    var touched = false;
    Object.keys(ATTRIBUTE_LABELS).forEach(function (shortKey) {
      if (Object.prototype.hasOwnProperty.call(params, shortKey)) {
        captured[shortKey] = params[shortKey];
        touched = true;
      }
    });
    if (!touched) return readStored();
    var merged = Object.assign({}, readStored(), captured);
    writeStored(merged);
    return merged;
  }

  function hasParams(obj) {
    if (!obj) return false;
    for (var k in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== '' && obj[k] != null) {
        return true;
      }
    }
    return false;
  }

  function buildCartAttributes(stored) {
    var attrs = {};
    Object.keys(ATTRIBUTE_LABELS).forEach(function (shortKey) {
      var value = stored[shortKey];
      if (value !== undefined && value !== null && value !== '') {
        attrs[ATTRIBUTE_LABELS[shortKey]] = value;
      }
    });
    return attrs;
  }

  function buildLineItemProperties(stored) {
    // Keep line-item properties small: only the ones that identify the benefit.
    var props = {};
    if (stored.vn) props['Vennotennummer'] = stored.vn;
    if (stored.nb) props['Cera benefit code'] = stored.nb;
    return props;
  }

  function buildCheckoutUrl(baseUrl) {
    var stored = readStored();
    var url = baseUrl || '/checkout';
    if (!hasParams(stored)) return url;

    var parts = [];
    function push(key, value) {
      if (value === undefined || value === null || value === '') return;
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    }

    // Email: Shopify's classic checkout accepts checkout[email], but the new one-page
    // checkout often ignores it and only honours the short `email` param. Send both
    // and the one that matches the active checkout version wins. We push them first
    // so nothing can override them further down the query string.
    push('checkout[email]', stored.ve);
    push('email', stored.ve);
    push('checkout[shipping_address][first_name]', stored.fn);
    push('checkout[shipping_address][last_name]', stored.ln);

    var address1 = '';
    if (stored.st) address1 = stored.st;
    if (stored.nr) address1 = address1 ? address1 + ' ' + stored.nr : stored.nr;
    push('checkout[shipping_address][address1]', address1);
    push('checkout[shipping_address][address2]', stored.bn);
    push('checkout[shipping_address][city]', stored.gm);
    push('checkout[shipping_address][zip]', stored.pc);
    push('checkout[shipping_address][phone]', stored.tn);
    // All Cera members live in Belgium - prefill both the full name and ISO code for safety.
    push('checkout[shipping_address][country]', 'Belgium');
    push('checkout[shipping_address][country_code]', 'BE');

    if (!parts.length) return url;
    var joiner = url.indexOf('?') >= 0 ? '&' : '?';
    return url + joiner + parts.join('&');
  }

  function postJson(endpoint, body) {
    return fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) {
        return response.text().then(function (text) {
          throw new Error('Cera cart request failed: ' + response.status + ' ' + text);
        });
      }
      return response.json().catch(function () { return {}; });
    });
  }

  function updateCartAttributes(extra) {
    var stored = readStored();
    var attributes = buildCartAttributes(stored);
    if (extra && typeof extra === 'object') {
      Object.keys(extra).forEach(function (k) { attributes[k] = extra[k]; });
    }
    if (!Object.keys(attributes).length) return Promise.resolve(null);
    return postJson('/cart/update.js', { attributes: attributes });
  }

  function addToCart(variantId, quantity) {
    if (!variantId) return Promise.reject(new Error('Missing variantId'));
    var stored = readStored();
    return postJson('/cart/add.js', {
      id: Number(variantId),
      quantity: quantity || 1,
      properties: buildLineItemProperties(stored)
    });
  }

  // Cera offers are limited to one product per vennoot: check the live cart for any
  // line item that already carries the current Vennotennummer as a line-item property.
  // Resolves with the matching cart item (truthy) when a duplicate is found, or null otherwise.
  // Resolves with null on network failure so the regular flow keeps working.
  function findCurrentVennotInCart() {
    var stored = readStored();
    var vn = stored && stored.vn;
    if (!vn) return Promise.resolve(null);

    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) return null;
        return response.json();
      })
      .then(function (cart) {
        if (!cart || !cart.items) return null;
        for (var i = 0; i < cart.items.length; i++) {
          var item = cart.items[i];
          var props = item && item.properties;
          if (props && String(props['Vennotennummer']) === String(vn)) {
            return item;
          }
        }
        return null;
      })
      .catch(function (err) {
        if (window.console && console.warn) {
          console.warn('[cera] unable to inspect cart', err);
        }
        return null;
      });
  }

  function goToCheckout(variantId, options) {
    options = options || {};
    var quantity = options.quantity || 1;
    var chain = variantId
      ? addToCart(variantId, quantity)
      : Promise.resolve(null);

    return chain
      .then(function () { return updateCartAttributes(options.extraAttributes); })
      .then(function () {
        window.location.href = buildCheckoutUrl('/checkout');
      })
      .catch(function (err) {
        if (window.console && console.error) {
          console.error('[cera] checkout flow error', err);
        }
        // Fallback: still try to reach the checkout so the user isn't stuck on the product page.
        window.location.href = buildCheckoutUrl('/checkout');
      });
  }

  function interceptCheckoutClicks() {
    document.addEventListener('click', function (event) {
      var el = event.target;
      if (!el || !el.closest) return;
      var trigger = el.closest('a.checkout-btn, button[name="checkout"]');
      if (!trigger) return;
      if (!hasParams(readStored())) return;

      event.preventDefault();
      // If the click originated inside a form (cart page), keep the note / company fields
      // by posting the form first - but replace its action so we can control the redirect.
      var form = trigger.form || (trigger.closest ? trigger.closest('form') : null);
      var submitForm = form && form.action && form.action.indexOf('/cart') !== -1;

      var prepare = submitForm
        ? postFormUpdate(form).then(function () { return updateCartAttributes(); })
        : updateCartAttributes();

      prepare
        .catch(function (err) {
          if (window.console && console.warn) console.warn('[cera] cart update failed', err);
        })
        .then(function () {
          window.location.href = buildCheckoutUrl('/checkout');
        });
    }, true);
  }

  function postFormUpdate(form) {
    try {
      var formData = new FormData(form);
      // Strip "checkout" submit button so Shopify doesn't redirect on /cart.
      formData.delete('checkout');
      return fetch('/cart/update', {
        method: 'POST',
        credentials: 'same-origin',
        body: formData
      }).then(function () { return null; });
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  // Public API.
  window.ceraCampaign = {
    STORAGE_KEY: STORAGE_KEY,
    capture: captureFromUrl,
    getParams: readStored,
    clear: function () { try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {} },
    buildCheckoutUrl: buildCheckoutUrl,
    updateCartAttributes: updateCartAttributes,
    addToCart: addToCart,
    goToCheckout: goToCheckout,
    findCurrentVennotInCart: findCurrentVennotInCart
  };

  captureFromUrl();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', interceptCheckoutClicks);
  } else {
    interceptCheckoutClicks();
  }
})();
